import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Top-level React error boundary. Without one, any uncaught render error
 * unmounts the whole tree and leaves a blank white page with nothing in the
 * console. This catches it, logs it, and shows the message + stack so a failure
 * is diagnosable instead of silent.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[worklogix] render error:', error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (error !== null) {
      return (
        <div
          role="alert"
          style={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            fontFamily: 'system-ui, sans-serif',
            color: '#c50f1f',
            background: '#fff',
            textAlign: 'center',
          }}
        >
          <h1 style={{ margin: 0, fontSize: 20 }}>WorkLogix hit an error</h1>
          <p style={{ margin: 0, color: '#242424', maxWidth: 640 }}>{error.message}</p>
          <pre
            style={{
              maxWidth: '90vw',
              maxHeight: '40vh',
              overflow: 'auto',
              fontSize: 12,
              color: '#605e5c',
              background: '#f5f5f5',
              padding: 12,
              borderRadius: 6,
              textAlign: 'left',
            }}
          >
            {error.stack}
          </pre>
        </div>
      );
    }
    return this.props.children;
  }
}
